import { DialogTitle } from "@headlessui/react";
import { useEffect, useId, useRef, useState } from "react";
import { toast } from "sonner";
import { Toaster } from "~/components/Toaster";
import { Link, useRevalidator } from "react-router";
import type { EventFragment, PurchaserBookingFragment } from "~/__generated__/graphql/graphql";
import { Copy, Send } from "lucide-react";
import { Avatar } from "~/components/Avatar";
import { GuestStatusBadge } from "~/components/GuestStatusBadge";
import { PrimaryButton, SecondaryButton } from "~/components/Button";
import { Text } from "~/components/Text";
import PendingFlowerImage from "~/images/yellow-flower.png";
import GreenFlowerImage from "~/images/green-flower.png";
import { FlowerCallout } from "~/components/FlowerCallout";
import { useRootLocale } from "~/root";
import { formatLocalizedDate } from "~/utils/date";
import { purchaserBookingState } from "./purchaser-booking-state";

export function PurchaserBookingStatus({
  initial,
  plan,
}: {
  initial: PurchaserBookingFragment;
  plan: EventFragment;
}) {
  const toasterId = useId();
  const [registration, setRegistration] = useState(initial);
  const [error, setError] = useState<string | null>(null);
  const [sharing, setSharing] = useState(false);
  const [canShare, setCanShare] = useState(false);
  useEffect(() => {
    setCanShare(typeof navigator.share === "function");
  }, []);
  const [shareFeedback, setShareFeedback] = useState<string | null>(null);
  const revision = useRef({ value: 0 });
  const active = useRef(true);
  const revalidator = useRevalidator();
  const revalidatorRef = useRef(revalidator);
  revalidatorRef.current = revalidator;
  const locale = useRootLocale();
  useEffect(() => {
    const revisionState = revision.current;
    const current = ++revisionState.value;
    active.current = true;
    setRegistration(initial);
    setError(null);
    setSharing(false);
    setShareFeedback(null);
    const refresh = async () => {
      try {
        const form = new FormData();
        form.set("registrationID", initial.id);
        const response = await fetch(`/api/events/${initial.eventID}/booking`, {
          method: "POST",
          body: form,
        });
        const result = await response.json();
        if (!active.current || revision.current.value !== current) return;
        if (result.ok && result.registration?.id === initial.id) {
          setRegistration(result.registration);
          setError(null);
          if (purchaserBookingState(result.registration) !== purchaserBookingState(initial)) {
            await revalidatorRef.current.revalidate();
          }
        } else setError("We couldn't refresh this booking. Try again shortly.");
      } catch {
        if (active.current && revision.current.value === current)
          setError("We couldn't refresh this booking. Try again shortly.");
      }
    };
    const timer = setInterval(() => {
      if (document.visibilityState !== "hidden") void refresh();
    }, 5000);
    const onVisible = () => {
      if (document.visibilityState !== "hidden") void refresh();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      active.current = false;
      revisionState.value++;
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [initial]);
  const state = purchaserBookingState(registration);
  const share = async (copy: boolean) => {
    const current = revision.current.value;
    const copying = copy || typeof navigator.share !== "function";
    let linkFailed = false;
    let nativeShareStarted = false;
    setSharing(true);
    setShareFeedback(null);
    try {
      const form = new FormData();
      form.set("registrationID", registration.id);
      form.set("intent", "share");
      const link = fetch(`/api/events/${registration.eventID}/booking`, {
        method: "POST", body: form, cache: "no-store",
      }).then(async response => {
        if (!response.ok) throw new Error("unavailable");
        const result = await response.json();
        if (!active.current || revision.current.value !== current || !result.ok || typeof result.url !== "string" || !result.url)
          throw new Error("unavailable");
        return result.url;
      }).catch(reason => {
        linkFailed = true;
        throw reason;
      });
      // Permission can be denied before the link read settles. Keep its rejection
      // handled even if the browser never consumes the ClipboardItem's data.
      void link.catch(() => {});
      // Invoke the clipboard during the click, before the network await, so Safari
      // retains the user gesture. The bearer stays out of state and persistent storage.
      if (copying && typeof ClipboardItem !== "undefined" && typeof navigator.clipboard?.write === "function") {
        const content = link.then(url => new Blob([url], { type: "text/plain" }));
        void content.catch(() => {});
        await navigator.clipboard.write([new ClipboardItem({
          "text/plain": content,
        })]);
      } else {
        const url = await link;
        if (!copying) {
          nativeShareStarted = true;
          await navigator.share({
            title: "Claim your place",
            text: `Got you a place at ${plan.title}, claim it here → ${url}`,
          });
        } else {
          if (typeof navigator.clipboard?.writeText !== "function") throw new Error("unavailable");
          await navigator.clipboard.writeText(url);
        }
      }
      if (active.current && revision.current.value === current && copying)
        toast.success("Link copied", { toasterId });
    } catch (reason) {
      if (
        active.current &&
        revision.current.value === current &&
        !(nativeShareStarted && reason instanceof DOMException && reason.name === "AbortError")
      )
        setShareFeedback(linkFailed ? "We couldn't get the link. Try again."
          : copying ? "We couldn't copy the link. Try again." : "We couldn't share the link. Try again.");
    } finally {
      if (active.current && revision.current.value === current)
        setSharing(false);
    }
  };
  const tickets = registration.reservations?.filter(ticket => ticket.state !== "CANCELLED") ?? [];
  const hasUnclaimed = tickets.some(ticket => !ticket.isPurchaser && ticket.claimState === "AVAILABLE");
  const purchaserGoing = tickets.some(ticket => ticket.isPurchaser && ticket.state === "CONFIRMED");
  const hostName = plan.hosts.find(host => host.primary)?.user.firstName?.trim() || undefined;
  const openAppLink = `/plans/${registration.eventID}/app?open=1`;
  const canSendTickets = state !== "cancelled" && !!registration.status.submittedAt && hasUnclaimed;
  const deadline = state === "pending" ? registration.status.decisionExpiresAt : null;
  const formattedDeadline = deadline
    ? `${formatLocalizedDate(deadline, "time", locale).replace(":00", "").replace(/\s/g, "")} ${formatLocalizedDate(deadline, "weekday", locale)}`
    : null;
  return (
    <div className="mt-4 flex flex-col gap-4">
      <div className="text-center">
        {(state === "pending" || state === "confirmed") && (
          <img src={state === "confirmed" ? GreenFlowerImage : PendingFlowerImage} alt="" width={86} height={70} className="mx-auto mb-3 h-[70px] w-[86px] object-contain" />
        )}
        <Text as={DialogTitle} variant="primary" size="sm">
          {state === "confirmed"
            ? purchaserGoing ? "You’re going" : "Your group is booked"
            : state === "pending" ? "Place requested"
              : state === "cancelled" ? "Booking unavailable" : "Checking your booking"}
        </Text>
        <Text size="sm" className="mt-2">
          {state === "confirmed"
            ? purchaserGoing
              ? registration.status.approvalRequired === true
                ? `${hostName ?? "The host"} has approved your booking. Your group’s tickets are confirmed.`
                : "Your group’s tickets are confirmed."
              : hasUnclaimed ? "Your tickets are ready. Invite your friends to claim theirs." : "Your group is all set. See you there."
            : state === "pending"
              ? `${hostName ?? "The host"}’s got it and usually replies within a day.`
              : state === "cancelled"
                ? "This booking is no longer active. Open Butter for the details."
                : "We’re checking your booking. This should only take a moment."}
        </Text>
      </div>
      {(state === "pending" || canSendTickets) && (
        <FlowerCallout
          label={state === "pending" ? "Host approval" : "Share your tickets"}
          title={canSendTickets ? "Send your places now" : undefined}
        >
          {state === "pending" ? <>
            Complete profiles help {hostName ?? "the host"} say yes.
            {formattedDeadline && <>
              {" If they haven't by "}
              <Text as="span" size="sm" weight="bold">{formattedDeadline}</Text>
              {", the request lapses and you won't be charged."}
            </>}
          </> : "Share one link. Your friends choose their name to claim their ticket."}
        </FlowerCallout>
      )}
      <section aria-label="Your tickets">
        <ul className="flex flex-col gap-2">
          {tickets.map(reservation => {
            const name = reservation.isPurchaser ? "Yours" : reservation.user?.name ?? reservation.recipientName ?? "Guest";
            const unclaimed = reservation.claimState === "AVAILABLE";
            const statusLabel = unclaimed ? "Unclaimed"
              : reservation.state === "CONFIRMED" ? "Going"
                : state === "pending" ? "Claimed"
                  : "Processing";
            return (
              <li key={reservation.id} className="flex items-center gap-3 rounded-xl bg-white p-3 shadow-[0_4px_18px] shadow-butter-green/9">
                <Avatar user={{ name: reservation.user?.name || name, profileImageUrl: reservation.user?.profileImageUrl }} size={41} className="shrink-0" />
                <div className="min-w-0 flex-1">
                  <Text size="sm" weight="bold" className="wrap-break-word">{name}</Text>
                  <Text size="xs" color="green" className="mt-0.5">
                    {unclaimed ? "Ticket to claim" : reservation.variation?.name ?? "Ticket"}
                  </Text>
                </div>
                <GuestStatusBadge
                  label={statusLabel}
                  tone={unclaimed ? "pink" : "green"}
                  claimState={unclaimed ? "unclaimed" : statusLabel === "Claimed" ? "claimed" : undefined}
                />
              </li>
            );
          })}
        </ul>
      </section>
      {canSendTickets && (
        <section aria-label="Share booking link" className="text-center">
          <div className="flex items-stretch gap-2">
            <PrimaryButton className="min-h-12 min-w-0 flex-1 font-semibold" loading={sharing} disabled={sharing} onClick={() => void share(true)}>
              <Copy aria-hidden className="size-4" />
              Copy link
            </PrimaryButton>
            {canShare && (
              <SecondaryButton disabled={sharing} onClick={() => void share(false)} className="min-h-12 w-auto! border! font-semibold!">
                <Send aria-hidden className="size-4" />
                Send link
              </SecondaryButton>
            )}
          </div>
          {shareFeedback && <Text role="status" size="sm" className="mt-2">{shareFeedback}</Text>}
        </section>
      )}
      <Toaster id={toasterId} />
      {error && <Text role="alert" size="sm">{error}</Text>}
      <div className="flex flex-col items-center text-center">
        <Text size="xs">Keep track of your booking in Butter.</Text>
        <Link
          to={openAppLink}
          reloadDocument
          className="inline-flex min-h-11 items-center font-sans text-sm font-semibold text-butter-green underline decoration-butter-green/40 underline-offset-4 hover:decoration-butter-green focus-visible:outline-2 focus-visible:outline-offset-4"
        >
          Open in Butter
        </Link>
      </div>
    </div>
  );
}
